import React, { PureComponent } from "react";
import * as Icon from "react-feather";

import FilterRule from "./FilterRule";
import { newRule, newGroup } from "./filterTreeUtils";

/**
 * Renders one group with its logic toggle + children + "+ Rule" / "+ Group" buttons.
 * Recurses for nested groups (D365-style).
 *
 * Props:
 *   group     : { _id, type:'group', logic, children }
 *   schema, ctx
 *   isRoot    : true for the outermost group (can't be removed)
 *   onChange  : (patchedGroup) => void   // immutable update
 *   onRemove  : () => void               // only called when !isRoot
 *   depth     : number (for visual indentation; capped in CSS)
 */
class FilterGroup extends PureComponent {
	onLogicToggle = nextLogic => {
		if (nextLogic === this.props.group.logic) return;
		this.props.onChange({ ...this.props.group, logic: nextLogic });
	};

	onChildChange = (idx, patched) => {
		const children = this.props.group.children.slice();
		if (patched == null) {
			children.splice(idx, 1);
		} else {
			children[idx] = patched;
		}
		this.props.onChange({ ...this.props.group, children });
	};

	onChildRemove = idx => {
		const children = this.props.group.children.slice();
		children.splice(idx, 1);
		this.props.onChange({ ...this.props.group, children });
	};

	onAddRule = () => {
		this.props.onChange({
			...this.props.group,
			children: [...this.props.group.children, newRule()],
		});
	};

	onAddGroup = () => {
		this.props.onChange({
			...this.props.group,
			children: [...this.props.group.children, newGroup({ logic: "and", children: [newRule()] })],
		});
	};

	render() {
		const { group, schema, ctx, isRoot, depth = 0 } = this.props;
		const logic = group.logic === "or" ? "or" : "and";

		return (
			<div
				className={
					"filter-group " +
					(isRoot ? "filter-group--root " : "") +
					"filter-group--depth-" + Math.min(depth, 4)
				}
			>
				<div className="filter-group__header">
					<div className="filter-group__logic">
						<button
							type="button"
							className={
								"filter-group__logic-btn " +
								(logic === "and" ? "filter-group__logic-btn--on" : "")
							}
							onClick={() => this.onLogicToggle("and")}
						>
							AND
						</button>
						<button
							type="button"
							className={
								"filter-group__logic-btn " +
								(logic === "or" ? "filter-group__logic-btn--on" : "")
							}
							onClick={() => this.onLogicToggle("or")}
						>
							OR
						</button>
					</div>

					{!isRoot && (
						<button
							type="button"
							className="filter-group__remove"
							onClick={this.props.onRemove}
							title="Remove this group"
						>
							<Icon.Trash2 size={12} />
						</button>
					)}
				</div>

				<div className="filter-group__children">
					{group.children.length === 0 && (
						<div className="filter-group__empty">No rules yet — add one below.</div>
					)}

					{group.children.map((child, idx) => {
						if (child.type === "rule") {
							return (
								<FilterRule
									key={child._id}
									rule={child}
									schema={schema}
									ctx={ctx}
									onChange={patched => this.onChildChange(idx, patched)}
									onRemove={() => this.onChildRemove(idx)}
								/>
							);
						}
						if (child.type === "group") {
							return (
								<FilterGroup
									key={child._id}
									group={child}
									schema={schema}
									ctx={ctx}
									depth={depth + 1}
									isRoot={false}
									onChange={patched => this.onChildChange(idx, patched)}
									onRemove={() => this.onChildRemove(idx)}
								/>
							);
						}
						return null;
					})}
				</div>

				<div className="filter-group__actions">
					<button type="button" className="filter-group__add" onClick={this.onAddRule}>
						<Icon.Plus size={11} />
						<span>Add rule</span>
					</button>
					<button type="button" className="filter-group__add" onClick={this.onAddGroup}>
						<Icon.Plus size={11} />
						<span>Add group</span>
					</button>
				</div>
			</div>
		);
	}
}

export default FilterGroup;
